/* =============================================================
   Atlavox infinite scroll for the collection product grid.

   Loads the next page of products via Shopify's Section Rendering
   API and appends the new items to #product-grid in place. The
   first N pages auto-load when the sentinel enters the viewport;
   after that a "Load more" button takes over so the footer stays
   reachable on huge collections.

   Coordinates with the smart-filter AJAX refresh: when filters
   change, Dawn's facets.js replaces #ProductGridContainer's
   innerHTML wholesale. We watch for that via MutationObserver
   and re-initialise from the freshly rendered (page-1) markup.
   ============================================================= */

(function () {
  'use strict';

  // DEBUG flag — set to true to log every step to the console. Off by
  // default for shipped behavior.
  var DBG = false;
  function log() {
    if (!DBG || !window.console) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[infinite-scroll]');
    console.log.apply(console, args);
  }

  log('script loaded');

  var visibleObserver = null;
  var isLoading = false;
  var autoLoadsConsumed = 0;

  function setup(container) {
    if (container.dataset.infiniteScrollInit === 'true') {
      log('setup: already initialised, skipping');
      return;
    }
    container.dataset.infiniteScrollInit = 'true';
    log('setup: container found', {
      nextUrl: container.dataset.nextUrl,
      sectionId: container.dataset.sectionId,
      currentPage: container.dataset.currentPage,
      autoPages: container.dataset.autoPages,
    });

    autoLoadsConsumed = 0;

    var sentinel = container.querySelector('[data-infinite-scroll-sentinel]');
    var button = container.querySelector('[data-infinite-scroll-button]');
    log('setup: sentinel found =', !!sentinel, ', button found =', !!button);

    if (button) {
      button.addEventListener('click', function () { loadMore(container); });
    }

    if (sentinel && 'IntersectionObserver' in window) {
      if (visibleObserver) visibleObserver.disconnect();
      visibleObserver = new IntersectionObserver(function (entries) {
        log('intersect: isIntersecting =', entries[0].isIntersecting,
            ', autoLoadsConsumed =', autoLoadsConsumed,
            ', autoCap =', container.dataset.autoPages);
        if (!entries[0].isIntersecting) return;
        maybeAutoLoad(container);
      }, { rootMargin: '400px 0px' });
      visibleObserver.observe(sentinel);
      log('setup: observer attached to sentinel');
    } else {
      log('setup: NOT attaching observer — sentinel missing or no IntersectionObserver');
    }

    // Log sentinel's initial position relative to viewport so we can
    // confirm it's actually below the fold (and how far).
    if (sentinel) {
      var rect = sentinel.getBoundingClientRect();
      log('sentinel position on setup: top=', rect.top, 'viewport h=', window.innerHeight,
          'distance below viewport=', rect.top - window.innerHeight);
    }

    updateButtonVisibility(container);
  }

  function maybeAutoLoad(container) {
    // Bail silently if we're mid-fetch (avoid spamming "already loading"
    // for every scroll/intersect event) or there's nothing left to load.
    if (isLoading) return;
    if (!container.dataset.nextUrl) return;
    var autoCap = parseInt(container.dataset.autoPages || '3', 10);
    if (autoLoadsConsumed < autoCap) {
      autoLoadsConsumed++;
      loadMore(container);
    }
    // Past the cap: do nothing. The Load more button (which is now
    // visible) takes over from here.
  }

  // ----- Scroll-based fallback in case IntersectionObserver doesn't
  //       fire for our specific layout (some Shopify apps mess with the
  //       body's scroll container; this is layout-agnostic). -----
  var scrollChecking = false;
  function onScroll() {
    if (scrollChecking) return;
    scrollChecking = true;
    window.requestAnimationFrame(function () {
      scrollChecking = false;
      var containers = document.querySelectorAll('[data-infinite-scroll]');
      var stillActive = false;
      for (var i = 0; i < containers.length; i++) {
        var c = containers[i];
        if (!c.dataset.nextUrl) continue;
        stillActive = true;
        var sentinel = c.querySelector('[data-infinite-scroll-sentinel]');
        if (!sentinel) continue;
        var rect = sentinel.getBoundingClientRect();
        // Trigger when sentinel comes within 400px of viewport bottom
        if (rect.top < window.innerHeight + 400 && rect.bottom > -100) {
          maybeAutoLoad(c);
        }
      }
      // If nothing on the page can still load, detach the listener so
      // we don't waste cycles on every scroll for the rest of the visit.
      if (!stillActive) {
        window.removeEventListener('scroll', onScroll);
        log('scroll fallback: nothing left to load, detaching listener');
      }
    });
  }
  window.addEventListener('scroll', onScroll, { passive: true });
  log('scroll fallback listener attached');

  function updateButtonVisibility(container) {
    var button = container.querySelector('[data-infinite-scroll-button]');
    if (!button) return;
    var nextUrl = container.dataset.nextUrl;
    if (!nextUrl) {
      button.style.display = 'none';
      return;
    }
    // Show button once we've used the auto-load quota
    var autoCap = parseInt(container.dataset.autoPages || '3', 10);
    button.style.display = autoLoadsConsumed >= autoCap ? '' : 'none';
  }

  function loadMore(container) {
    if (isLoading) { log('loadMore: already loading, skip'); return; }
    var nextUrl = container.dataset.nextUrl;
    if (!nextUrl) { log('loadMore: no nextUrl on container — done'); return; }
    var sectionId = container.dataset.sectionId;
    if (!sectionId) { log('loadMore: no sectionId on container — aborting'); return; }
    log('loadMore: fetching nextUrl=', nextUrl, 'sectionId=', sectionId);

    isLoading = true;
    container.classList.add('is-loading');

    var url;
    try {
      url = new URL(nextUrl, window.location.origin);
    } catch (e) {
      isLoading = false;
      container.classList.remove('is-loading');
      console.error('[infinite-scroll] bad next url:', nextUrl);
      return;
    }
    url.searchParams.set('section_id', sectionId);

    fetch(url.toString())
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var newItems = doc.querySelectorAll('#product-grid > .grid__item');
        var grid = document.getElementById('product-grid');
        if (!grid || newItems.length === 0) {
          // Out of products
          container.dataset.nextUrl = '';
          if (visibleObserver) visibleObserver.disconnect();
          updateButtonVisibility(container);
          return;
        }
        // Append new items (cloned to avoid moving from parsed doc)
        for (var i = 0; i < newItems.length; i++) {
          grid.appendChild(newItems[i]);
        }
        // Refresh next URL from the response's container
        var newContainer = doc.querySelector('[data-infinite-scroll]');
        container.dataset.nextUrl = newContainer && newContainer.dataset.nextUrl
          ? newContainer.dataset.nextUrl
          : '';
        if (!container.dataset.nextUrl && visibleObserver) {
          visibleObserver.disconnect();
        }
        updateButtonVisibility(container);
      })
      .catch(function (err) {
        console.error('[infinite-scroll] fetch failed:', err);
      })
      .then(function () {
        isLoading = false;
        container.classList.remove('is-loading');
      });
  }

  function initAll() {
    var containers = document.querySelectorAll('[data-infinite-scroll]');
    log('initAll: found', containers.length, 'container(s)');
    containers.forEach(setup);
  }

  // Initial setup
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Watch for grid container replacement (Dawn's facets AJAX refresh
  // replaces #ProductGridContainer.innerHTML on every filter change).
  //
  // CAUTION: third-party apps (Easify, Bixgrow, etc.) inject and mutate
  // nodes inside #ProductGridContainer freely. We must NOT tear down our
  // observer for those — only react when a brand-new uninitialized
  // [data-infinite-scroll] container actually appears in the DOM.
  function watchForGridReset() {
    var gridContainer = document.getElementById('ProductGridContainer');
    if (!gridContainer) return;
    var mo = new MutationObserver(function () {
      var uninitContainer = document.querySelector(
        '[data-infinite-scroll]:not([data-infinite-scroll-init="true"])'
      );
      if (!uninitContainer) {
        log('grid mutation: no new container, ignoring');
        return;
      }
      log('grid mutation: new container detected, re-init');
      autoLoadsConsumed = 0;
      if (visibleObserver) {
        visibleObserver.disconnect();
        visibleObserver = null;
      }
      setup(uninitContainer);
    });
    mo.observe(gridContainer, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchForGridReset);
  } else {
    watchForGridReset();
  }
})();
